import * as THREE from "three";
import { SoftBox } from "./roundedBoxes";
import { LANE_POSITIONS } from "@shared/constants";
import { sfx } from "../audio";
import type { Path } from "./Path";

// Throttle ball-bounce SFX globally so a wave of 5 balls bouncing on the same
// frame doesn't fire 5 overlapping plays.
let _lastBounceSfxAt = 0;
function ballBounceSfx(volume: number) {
  const now = performance.now();
  if (now - _lastBounceSfxAt < 80) return;
  _lastBounceSfxAt = now;
  sfx.ballBounce(volume);
}

export type ObstacleKind =
  | "barrier"
  | "crate"
  | "lava"
  | "log"
  | "swing-log"
  | "boulder"
  | "spike-roller"
  | "sweeper"
  | "bouncing-ball"
  | "snowball"
  | "spinner"
  | "ice-spinner";

export interface ObstacleUpdateArgs {
  dt: number;
  now: number;
  playerZ: number;
  /** Shared wall-clock since game start (ms). Identical on every client in
   *  multiplayer, so time-based animations (spinner rotation, ball respawn,
   *  cannon fire, swing-log oscillation) stay in lockstep across devices. */
  clockMs: number;
  /** Other obstacles in the scene (used for inter-obstacle physics, e.g. ball↔spinner). */
  peers?: ObstacleController[];
  /** Moving platforms in the level. Bouncing balls collide with them. */
  platforms?: ReadonlyArray<{
    s: number;
    topY: number;
    halfDepth: number;
    halfWidth: number;
    halfHeight: number;
    velocityS: number;
  }>;
}

export interface ObstaclePassArgs {
  jumping: boolean;
  sliding: boolean;
}

export interface ObstacleImpactArgs {
  now: number;
  playerS: number;
  playerLaneOffset: number;
  playerForwardVelocity: number;
}

export interface ObstacleContactArgs {
  playerS: number;
  playerLaneOffset: number;
  playerY: number;
  playerRadius: number;
  playerForwardVelocity: number;
  playerLateralVelocity: number;
}

export interface ObstacleContact {
  normalS: number;
  normalL: number;
  penetration: number;
  relativeSpeed: number;
  obstacleVelocityS: number;
  obstacleVelocityL: number;
  isForceful: boolean;
}

export interface ObstacleController {
  id: number;
  kind: ObstacleKind;
  mesh: THREE.Object3D;
  resolved: boolean;
  mass?: number;
  getLateralOffset?(): number;
  /** Collision radius for dynamic-vs-dynamic contact. Optional — defaults to
   *  ~0.88 (sqrt(3.1/4)) for legacy obstacles that didn't expose one. */
  getRadius?(): number;
  /** Forward velocity along the path (m/s). For ball-vs-ball impulse exchange. */
  getVelocityS?(): number;
  /** Lateral velocity (m/s). */
  getVelocityL?(): number;
  /** Vertical velocity (m/s) — primarily for balls. */
  getVelocityY?(): number;
  /** Apply a 3D velocity impulse (forward, lateral, vertical). For ball-vs-ball
   *  collisions we want to also kick the vertical component. */
  applyVelocityImpulse?(dS: number, dL: number, dY: number): void;
  lanes(): number[];
  getZ(): number;
  update(args: ObstacleUpdateArgs): void;
  passes(args: ObstaclePassArgs): boolean;
  hasForceImpact?(): boolean;
  impact?(args: ObstacleImpactArgs): void;
  applyPhysicsImpulse?(forward: number, lateral: number): void;
  getContact?(args: ObstacleContactArgs): ObstacleContact | null;
}

interface BaseOpts {
  id: number;
  z: number; // arc-length s along the path
  lane: number;
  path?: Path;
  /** Optional lateral offset in metres for spinner placement so two
   *  spinners can sit side by side at the same s. */
  lateral?: number;
  /** Optional spin-direction override. Default + (counter-clockwise). */
  spinDir?: 1 | -1;
  /** Long-arm spinner: arm reaches across the whole track. */
  long?: boolean;
}

/** Place a mesh at path arc length `s`, optionally with a lateral lane offset. */
function placeOnPath(mesh: THREE.Object3D, path: Path | undefined, s: number, laneOffset = 0) {
  if (!path) {
    mesh.position.set(laneOffset, 0, s);
    return;
  }
  const pos = path.positionAt(s, laneOffset);
  mesh.position.copy(pos);
  mesh.rotation.y = path.yawAt(s);
}

const PALETTE = {
  barrier: 0xef476f,
  barrierPost: 0x222222,
  crate: 0xb3753a,
  crateBand: 0x6f4624,
  lavaCool: 0x8a2c11,
  lavaHot: 0xff7a2b,
  log: 0x6f4a2a,
  logLeaf: 0x2f7d3a,
  swingLog: 0x8b5a2b,
  swingRope: 0x32251b,
  boulder: 0x6e6a64,
  spike: 0x9aa3b2,
  spikePoint: 0x444444,
  sweeperArm: 0x8bdc2d,
  sweeperPost: 0xff2ca4,
} as const;

/* ── BARRIER ────────────────────────────────────────────────────────────── */
class Barrier implements ObstacleController {
  kind: ObstacleKind = "barrier";
  mesh = new THREE.Group();
  resolved = false;
  mass = 8;
  private s: number;
  constructor(public id: number, z: number, public lane: number, path?: Path) {
    this.s = z;
    placeOnPath(this.mesh, path, z, LANE_POSITIONS[lane]!);
    const bar = new THREE.Mesh(
      new SoftBox(1.6, 0.7, 0.35),
      new THREE.MeshStandardMaterial({ color: PALETTE.barrier }),
    );
    bar.position.y = 1.4;
    bar.castShadow = true;
    this.mesh.add(bar);
    for (const x of [-0.7, 0.7]) {
      const post = new THREE.Mesh(
        new SoftBox(0.18, 1.5, 0.18),
        new THREE.MeshStandardMaterial({ color: PALETTE.barrierPost }),
      );
      post.position.set(x, 0.75, 0);
      post.castShadow = true;
      this.mesh.add(post);
    }
  }
  lanes() { return [this.lane]; }
  getZ() { return this.s; }
  update() {}
  passes({ sliding }: ObstaclePassArgs) { return sliding; }
}

/* ── CRATE ──────────────────────────────────────────────────────────────── */
class Crate implements ObstacleController {
  kind: ObstacleKind = "crate";
  mesh = new THREE.Group();
  resolved = false;
  mass = 2.5;
  private s: number;
  constructor(public id: number, z: number, public lane: number, path?: Path) {
    this.s = z;
    placeOnPath(this.mesh, path, z, LANE_POSITIONS[lane]!);
    const box = new THREE.Mesh(
      new SoftBox(1.4, 1.4, 1.4),
      new THREE.MeshStandardMaterial({ color: PALETTE.crate }),
    );
    box.position.y = 0.7;
    box.castShadow = true;
    this.mesh.add(box);
    const bandMat = new THREE.MeshStandardMaterial({ color: PALETTE.crateBand });
    const bandGeo = new SoftBox(1.42, 0.12, 0.12);
    for (const y of [0.3, 1.1]) {
      const b = new THREE.Mesh(bandGeo, bandMat);
      b.position.y = y;
      this.mesh.add(b);
    }
  }
  lanes() { return [this.lane]; }
  getZ() { return this.s; }
  update() {}
  passes({ jumping }: ObstaclePassArgs) { return jumping; }
}

/* ── LAVA ───────────────────────────────────────────────────────────────── */
class Lava implements ObstacleController {
  kind: ObstacleKind = "lava";
  mesh = new THREE.Group();
  resolved = false;
  mass = 20;
  private s: number;
  /** Forced to center lane (2). Pool spans [1,2,3]; ends never overhang the path. */
  public centerLane = 2;
  constructor(public id: number, z: number, _lane: number, path?: Path) {
    this.s = z;
    placeOnPath(this.mesh, path, z, 0);
    const width = 7;
    const mat = new THREE.MeshStandardMaterial({
      color: PALETTE.lavaHot,
      emissive: PALETTE.lavaHot,
      emissiveIntensity: 0.7,
    });
    const pool = new THREE.Mesh(new SoftBox(width, 0.15, 1.4), mat);
    pool.position.set(0, 0.08, 0);
    this.mesh.add(pool);
    const rimMat = new THREE.MeshStandardMaterial({ color: PALETTE.lavaCool });
    for (const dz of [-0.75, 0.75]) {
      const rim = new THREE.Mesh(new SoftBox(width, 0.25, 0.18), rimMat);
      rim.position.set(0, 0.13, dz);
      this.mesh.add(rim);
    }
  }
  lanes() { return [1, 2, 3]; }
  getZ() { return this.s; }
  update() {}
  passes({ jumping }: ObstaclePassArgs) { return jumping; }
}

/* ── FALLING LOG (or just a standing tree, randomized) ──────────────────── */
class FallingLog implements ObstacleController {
  kind: ObstacleKind = "log";
  mesh = new THREE.Group();
  resolved = false;
  mass = 7;
  private state: "standing" | "falling" | "bouncing" | "fallen" | "decoration" = "standing";
  private fallT = 0;
  private side: 1 | -1;
  private centerLane: number;
  private willFall: boolean;
  private baseX: number;
  public onImpact: (() => void) | null = null;
  private impactFired = false;
  private s: number;

  constructor(public id: number, z: number, lane: number, path?: Path) {
    this.s = z;
    placeOnPath(this.mesh, path, z, 0);
    this.centerLane = Math.max(1, Math.min(LANE_POSITIONS.length - 2, lane));
    this.side = LANE_POSITIONS[this.centerLane]! < 0 ? -1 : 1;
    // Stand right at the path edge (was 7.5 — too far off, looked detached).
    // 6.0 sits just outside the parapet wall; falling 7m trunk reaches center.
    this.baseX = this.side * 6.0;
    // Deterministic per-id outcome so every client agrees on which logs are
    // decoration and which will actually fall — otherwise MP peers see one
    // player get hit by a "log" that's just standing there on their screen.
    this.willFall = ((id * 0x9E37) & 0xff) < 0xff * 0.6;
    if (!this.willFall) this.state = "decoration";

    const trunkGeo = new THREE.CylinderGeometry(0.45, 0.55, 7, 10);
    const trunkMat = new THREE.MeshStandardMaterial({ color: PALETTE.log });
    const trunk = new THREE.Mesh(trunkGeo, trunkMat);
    trunk.castShadow = true;
    trunk.position.set(this.baseX, 3.5, 0);
    this.mesh.add(trunk);

    const crown = new THREE.Mesh(
      new THREE.IcosahedronGeometry(1.6, 0),
      new THREE.MeshStandardMaterial({ color: PALETTE.logLeaf, flatShading: true }),
    );
    crown.position.set(this.baseX, 7, 0);
    crown.castShadow = true;
    this.mesh.add(crown);
  }
  lanes() {
    if (this.state === "standing" || this.state === "decoration") return [];
    return [this.centerLane - 1, this.centerLane, this.centerLane + 1].filter(
      (l) => l >= 0 && l < LANE_POSITIONS.length,
    );
  }
  getZ() { return this.s; }

  private rotateAroundBase(angle: number) {
    const cs = Math.cos(angle);
    const sn = Math.sin(angle);
    this.mesh.children.forEach((c) => {
      if (c.userData.baseX === undefined) {
        c.userData.baseX = c.position.x;
        c.userData.baseY = c.position.y;
      }
      const localX = c.userData.baseX - this.baseX;
      const localY = c.userData.baseY;
      c.position.x = this.baseX + localX * cs - localY * sn;
      c.position.y = localX * sn + localY * cs;
      c.rotation.z = angle;
    });
  }

  update({ dt, now, playerZ }: ObstacleUpdateArgs) {
    if (this.state === "decoration") return;
    const dist = this.s - playerZ;
    if (this.state === "standing" && dist < 18 && dist > 4) {
      this.state = "falling";
      this.fallT = 0;
    }
    if (this.state === "falling" || this.state === "bouncing") {
      this.fallT += dt;
      const fallDur = 0.55;
      let angle: number;
      if (this.fallT < fallDur) {
        const t = this.fallT / fallDur;
        const eased = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
        angle = eased * (Math.PI / 2) * this.side;
      } else {
        if (!this.impactFired) {
          this.impactFired = true;
          this.onImpact?.();
        }
        const t = Math.min(1, (this.fallT - fallDur) / 0.5);
        const decay = Math.exp(-3 * t);
        const wobble = Math.sin(t * Math.PI * 4) * 0.08 * decay;
        angle = (Math.PI / 2 + wobble) * this.side;
        this.state = "bouncing";
        if (t >= 1) this.state = "fallen";
      }
      this.rotateAroundBase(angle);
    }
  }
  passes({ jumping }: ObstaclePassArgs) {
    if (this.state === "standing" || this.state === "decoration") return true;
    if (this.state === "falling" || this.state === "bouncing") return false;
    return jumping;
  }
  hasForceImpact() {
    return this.state === "falling" || this.state === "bouncing";
  }
}

/* ── SWINGING LOG ───────────────────────────────────────────────────────── */
class SwingingLog implements ObstacleController {
  kind: ObstacleKind = "swing-log";
  mesh = new THREE.Group();
  resolved = false;
  mass = 90;
  private frame = new THREE.Group();
  private log = new THREE.Group();
  private s: number;
  private phase = 0;
  /** Phase offset captured per-id so different swings start at different
   *  angles, but each is fully determined by clockMs alone. */
  private phase0 = 0;
  private swing = 0;
  private swingVel = 0;
  private amplitude = 2.95;
  private angularVelocity = 1.55;
  private logHalfLength = 2.35;
  private logRadius = 0.42;

  constructor(public id: number, z: number, _lane: number, path?: Path) {
    this.s = z;
    this.phase0 = (id * 1.21) % (Math.PI * 2);
    this.phase = this.phase0;
    placeOnPath(this.mesh, path, z, 0);

    const postMat = new THREE.MeshStandardMaterial({ color: 0x7c3aed, roughness: 0.45 });
    const ropeMat = new THREE.MeshStandardMaterial({ color: PALETTE.swingRope, roughness: 0.7 });
    for (const x of [-2.85, 2.85]) {
      const post = new THREE.Mesh(new SoftBox(0.32, 4.2, 0.32), postMat);
      post.position.set(x, 2.1, 0);
      post.castShadow = true;
      this.frame.add(post);
    }
    const beam = new THREE.Mesh(new SoftBox(6.3, 0.34, 0.34), postMat);
    beam.position.set(0, 4.12, 0);
    beam.castShadow = true;
    this.frame.add(beam);
    this.mesh.add(this.frame);

    for (const zOff of [-1.65, 1.65]) {
      const rope = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.045, 2.9, 8), ropeMat);
      rope.position.set(0, 2.45, zOff);
      rope.castShadow = true;
      this.log.add(rope);
    }
    const trunk = new THREE.Mesh(
      new THREE.CylinderGeometry(this.logRadius, this.logRadius, this.logHalfLength * 2, 18),
      new THREE.MeshStandardMaterial({ color: PALETTE.swingLog, roughness: 0.58 }),
    );
    trunk.rotation.x = Math.PI / 2;
    trunk.position.y = 0.95;
    trunk.castShadow = true;
    trunk.receiveShadow = true;
    this.log.add(trunk);
    this.mesh.add(this.log);
  }

  lanes() { return [1, 2, 3]; }
  getZ() { return this.s; }
  update({ clockMs }: ObstacleUpdateArgs) {
    // Deterministic phase from shared clock — both clients in MP see the
    // swing at exactly the same position.
    this.phase = this.phase0 + (clockMs / 1000) * this.angularVelocity;
    this.swing = Math.sin(this.phase) * this.amplitude;
    this.swingVel = Math.cos(this.phase) * this.amplitude * this.angularVelocity;
    this.log.position.x = this.swing;
    this.log.rotation.z = -this.swing * 0.12;
    this.log.position.y = 0.12 + Math.abs(Math.sin(this.phase)) * 0.1;
    this.frame.rotation.y = Math.sin(clockMs * 0.0011) * 0.015;
  }
  passes() { return false; }
  hasForceImpact() { return true; }
  getContact({
    playerS,
    playerLaneOffset,
    playerY,
    playerRadius,
    playerLateralVelocity,
  }: ObstacleContactArgs): ObstacleContact | null {
    if (playerY > 1.75) return null;
    const px = playerLaneOffset;
    const pz = playerS - this.s;
    const closestZ = THREE.MathUtils.clamp(pz, -this.logHalfLength, this.logHalfLength);
    const dx = px - this.swing;
    const dz = pz - closestZ;
    const radius = this.logRadius + playerRadius;
    const distSq = dx * dx + dz * dz;
    if (distSq > radius * radius) return null;
    const dist = Math.sqrt(Math.max(0.0001, distSq));
    const normalL = dx / dist;
    const normalS = dz / dist;
    const relL = this.swingVel - playerLateralVelocity;
    const relativeSpeed = Math.max(0, relL * normalL);
    return {
      normalS,
      normalL,
      penetration: radius - dist,
      relativeSpeed,
      obstacleVelocityS: 0,
      obstacleVelocityL: this.swingVel,
      isForceful: relativeSpeed > 1.0 || Math.abs(this.swingVel) > 2.2,
    };
  }
}

/* ── ROLLING BOULDER ────────────────────────────────────────────────────── */
class RollingBoulder implements ObstacleController {
  kind: ObstacleKind = "boulder";
  mesh: THREE.Group;
  resolved = false;
  mass = 18;
  private ball = new THREE.Group();
  private body: THREE.Mesh;
  private cannon = new THREE.Group();
  private state: "waiting" | "launched" | "rolling" = "waiting";
  private velocityS = 0;
  private velocityL = 0;
  private lateralOffset: number;
  private cannonOffset: number;
  private airY = 0;
  private airVel = 0;
  private lastGroundY = 0;
  private nextFireAt = 0;
  private firedAt = 0;
  private startS: number;
  private s: number;
  private path?: Path;
  private readonly fireIntervalMs = 5000;
  constructor(public id: number, z: number, public lane: number, path?: Path) {
    this.mesh = new THREE.Group();
    this.startS = z;
    this.s = z + 22;
    this.path = path;
    const laneOffset = LANE_POSITIONS[lane]!;
    this.cannonOffset = laneOffset < 0 ? -6.7 : 6.7;
    this.lateralOffset = this.cannonOffset;
    this.body = new THREE.Mesh(
      new THREE.IcosahedronGeometry(1.0, 1),
      new THREE.MeshStandardMaterial({ color: PALETTE.boulder, flatShading: true }),
    );
    this.body.position.y = 1.0;
    this.body.castShadow = true;
    this.ball.add(this.body);
    this.ball.visible = false;
    this.mesh.add(this.ball);

    const cannonMat = new THREE.MeshStandardMaterial({ color: 0x293241, roughness: 0.42, metalness: 0.35 });
    const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.48, 0.62, 2.2, 18), cannonMat);
    barrel.rotation.z = Math.PI / 2;
    barrel.position.set(this.cannonOffset < 0 ? 0.62 : -0.62, 1.2, 0);
    barrel.castShadow = true;
    this.cannon.add(barrel);
    const base = new THREE.Mesh(new SoftBox(1.5, 0.45, 1.2), cannonMat);
    base.position.set(0, 0.25, 0);
    base.castShadow = true;
    this.cannon.add(base);
    this.mesh.add(this.cannon);
    this.placeBody();
    this.lastGroundY = this.ball.position.y;
    this.placeCannon();
  }
  getLateralOffset() { return this.lateralOffset; }
  lanes() {
    if (Math.abs(this.lateralOffset) > 5.6) return [];
    const nearest = nearestLane(this.lateralOffset);
    const lanes = [nearest];
    const laneCenter = LANE_POSITIONS[nearest]!;
    if (Math.abs(this.lateralOffset - laneCenter) > 0.75) {
      const dir = this.lateralOffset > laneCenter ? 1 : -1;
      const next = nearest + dir;
      if (next >= 0 && next < LANE_POSITIONS.length) lanes.push(next);
    }
    return lanes;
  }
  getZ() { return this.s; }
  update({ dt, now, playerZ }: ObstacleUpdateArgs) {
    const activeRange = this.startS - playerZ < 32 && playerZ < this.startS + 150;
    if (this.state === "waiting") {
      this.s = this.startS + 22;
      this.lateralOffset = this.cannonOffset;
      this.airY = 0;
      this.airVel = 0;
      this.placeBody();
      if (activeRange && now >= this.nextFireAt) this.fire();
    }
    if (this.state === "launched" || this.state === "rolling") {
      const targetL = LANE_POSITIONS[this.lane]!;
      const prevGroundY = this.lastGroundY;
      this.s += this.velocityS * dt;
      this.lateralOffset += this.velocityL * dt;
      if (this.state === "launched" && Math.abs(this.lateralOffset - targetL) < 0.55) {
        this.state = "rolling";
        this.lateralOffset = targetL;
        this.velocityL *= 0.55;
      }
      if (this.state === "rolling") {
        const slope = this.path?.heightSlopeAt(this.s) ?? 0;
        this.velocityS += -slope * 22 * dt;
        this.velocityS *= Math.exp(-0.34 * dt);
        this.velocityL *= Math.exp(-0.7 * dt);
        if (Math.abs(this.velocityS) < 0.05) this.velocityS = 0;
        if (Math.abs(this.velocityL) < 0.05) this.velocityL = 0;
      }
      this.bounceWalls();
      this.updateVertical(dt, prevGroundY);
      this.placeBody();
      this.body.rotation.x += (this.velocityS * dt) / 1.0;
      this.body.rotation.z -= (this.velocityL * dt) / 1.0;
    }
    if (this.state !== "waiting" && now - this.firedAt >= this.fireIntervalMs) {
      this.resetForNextShot(now);
    } else if (this.state !== "waiting" && (this.s < playerZ - 8 || Math.abs(this.lateralOffset) > 7.4)) {
      this.resetForNextShot(now);
    }
  }
  passes() { return false; }
  hasForceImpact() {
    return this.state !== "waiting" && (Math.abs(this.velocityS) > 3 || Math.abs(this.velocityL) > 3);
  }
  getContact({
    playerS,
    playerLaneOffset,
    playerY,
    playerRadius,
    playerForwardVelocity,
    playerLateralVelocity,
  }: ObstacleContactArgs): ObstacleContact | null {
    if (this.state === "waiting") return null;
    if (playerY > 1.65 + this.airY) return null;
    const dz = playerS - this.s;
    const dl = playerLaneOffset - this.lateralOffset;
    const radius = 1.0 + playerRadius;
    const distSq = dz * dz + dl * dl;
    if (distSq > radius * radius) return null;
    const dist = Math.sqrt(Math.max(0.0001, distSq));
    const normalS = dz / dist;
    const normalL = dl / dist;
    const relS = this.velocityS - playerForwardVelocity;
    const relL = this.velocityL - playerLateralVelocity;
    const relativeSpeed = Math.max(0, relS * normalS + relL * normalL);
    return {
      normalS,
      normalL,
      penetration: radius - dist,
      relativeSpeed,
      obstacleVelocityS: this.velocityS,
      obstacleVelocityL: this.velocityL,
      isForceful: relativeSpeed > 2.4 || Math.hypot(this.velocityS, this.velocityL) > 6,
    };
  }
  impact({ playerForwardVelocity, playerLaneOffset }: ObstacleImpactArgs) {
    if (this.state === "waiting") return;
    const side = THREE.MathUtils.clamp(this.lateralOffset - playerLaneOffset, -1.2, 1.2);
    const currentSpeed = Math.max(8.5, Math.abs(this.velocityS));
    const playerPush = Math.max(0, playerForwardVelocity) * 0.08;
    this.velocityS = this.velocityS <= 0
      ? -Math.max(7.5, currentSpeed * 0.76 - playerPush)
      : Math.max(3, this.velocityS * 0.35);
    this.velocityL += side * 2.6;
  }
  applyPhysicsImpulse(forward: number, lateral: number) {
    if (this.state === "waiting") this.fire();
    this.velocityS += forward / this.mass;
    this.velocityL += lateral / this.mass;
  }
  private fire() {
    const laneOffset = LANE_POSITIONS[this.lane]!;
    this.state = "launched";
    this.resolved = false;
    this.firedAt = performance.now();
    this.s = this.startS + 22;
    this.lateralOffset = this.cannonOffset;
    this.airY = 0;
    this.airVel = 0;
    this.lastGroundY = this.path?.heightAt(this.s) ?? 0;
    const slope = this.path?.heightSlopeAt(this.s) ?? 0;
    this.velocityS = -5 - Math.max(0, slope) * 8;
    this.velocityL = Math.sign(laneOffset - this.cannonOffset) * 18;
    this.ball.visible = true;
    this.placeBody();
  }
  private resetForNextShot(now: number) {
    this.state = "waiting";
    this.resolved = true;
    this.velocityS = 0;
    this.velocityL = 0;
    this.ball.visible = false;
    this.nextFireAt = Math.max(now, this.firedAt + this.fireIntervalMs);
  }
  private bounceWalls() {
    const half = 5.35;
    if (this.lateralOffset < -half) {
      this.lateralOffset = -half;
      this.velocityL = Math.abs(this.velocityL) * 0.82;
    } else if (this.lateralOffset > half) {
      this.lateralOffset = half;
      this.velocityL = -Math.abs(this.velocityL) * 0.82;
    }
  }
  private placeBody() {
    placeOnPath(this.ball, this.path, this.s, this.lateralOffset);
    this.ball.position.y += this.airY;
  }
  private placeCannon() {
    placeOnPath(this.cannon, this.path, this.startS + 22, this.cannonOffset);
    this.cannon.rotation.y += this.cannonOffset < 0 ? -Math.PI / 2 : Math.PI / 2;
  }
  private updateVertical(dt: number, prevGroundY: number) {
    const groundY = this.path?.heightAt(this.s) ?? 0;
    const groundDelta = groundY - prevGroundY;
    const steepWall = groundDelta > 0.7 && this.airY < 0.15;
    if (steepWall) {
      this.s -= this.velocityS * dt * 0.9;
      this.velocityS = Math.abs(this.velocityS) * 0.18;
      this.velocityL *= 0.45;
      this.airVel = 2.2;
      this.airY = 0.08;
      return;
    }
    if (groundDelta < -0.55 && this.airY < 0.1) {
      this.airY += -groundDelta * 0.45;
      this.airVel = Math.max(this.airVel, 1.2);
    }
    if (this.airY > 0 || this.airVel !== 0) {
      this.airVel -= 14 * dt;
      this.airY += this.airVel * dt;
      if (this.airY <= 0) {
        this.airY = 0;
        this.airVel = Math.abs(this.airVel) * 0.28;
        this.velocityS *= 0.82;
        this.velocityL *= 0.78;
        if (this.airVel < 1.1) this.airVel = 0;
      }
    }
    this.lastGroundY = groundY;
  }
}

/* ── SPIKE ROLLER ───────────────────────────────────────────────────────── */
class SpikeRoller implements ObstacleController {
  kind: ObstacleKind = "spike-roller";
  mesh = new THREE.Group();
  resolved = false;
  mass = 10;
  private roller: THREE.Group;
  private s: number;
  constructor(public id: number, z: number, _lane: number, path?: Path) {
    this.s = z;
    placeOnPath(this.mesh, path, z, 0);
    this.roller = new THREE.Group();
    // Always centered on the path so end posts never overhang on a corner.
    this.roller.position.set(0, 1.0, 0);
    const cyl = new THREE.Mesh(
      new THREE.CylinderGeometry(0.7, 0.7, 6.5, 14),
      new THREE.MeshStandardMaterial({ color: PALETTE.spike }),
    );
    cyl.rotation.z = Math.PI / 2;
    cyl.castShadow = true;
    this.roller.add(cyl);
    const spikeMat = new THREE.MeshStandardMaterial({ color: PALETTE.spikePoint });
    const spikeGeo = new THREE.ConeGeometry(0.18, 0.5, 6);
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2;
      for (let xPos = -3; xPos <= 3; xPos += 0.85) {
        const s = new THREE.Mesh(spikeGeo, spikeMat);
        s.position.set(xPos, Math.sin(angle) * 0.95, Math.cos(angle) * 0.95);
        s.rotation.set(Math.PI / 2 + angle, 0, Math.PI / 2);
        this.roller.add(s);
      }
    }
    this.mesh.add(this.roller);
    const postMat = new THREE.MeshStandardMaterial({ color: 0x444444 });
    for (const x of [-3.5, 3.5]) {
      const post = new THREE.Mesh(new SoftBox(0.4, 2.2, 0.4), postMat);
      post.position.set(x, 1.1, 0);
      this.mesh.add(post);
    }
  }
  lanes() { return [1, 2, 3]; }
  getZ() { return this.s; }
  update({ clockMs }: ObstacleUpdateArgs) {
    // Absolute rotation = (clockMs/1000) * 4 rad/s + per-id phase offset.
    this.roller.rotation.x = ((this.id * 0.79) % (Math.PI * 2)) + (clockMs / 1000) * 4;
  }
  passes({ jumping, sliding }: ObstaclePassArgs) {
    return jumping || sliding;
  }
}

/* ── ROTATING SWEEPER ───────────────────────────────────────────────────── */
class RotatingSweeper implements ObstacleController {
  kind: ObstacleKind = "sweeper";
  mesh = new THREE.Group();
  resolved = false;
  mass = 14;
  private armPivot = new THREE.Group();
  private s: number;
  private spin = 0;
  private angularVelocity = 2.45;
  private armHalfLength = 5.2;
  private armRadius = 0.28;

  constructor(public id: number, z: number, _lane: number, path?: Path) {
    this.s = z;
    this.spinPhase0 = (id * 0.91) % (Math.PI * 2);
    placeOnPath(this.mesh, path, z, 0);

    const baseMat = new THREE.MeshStandardMaterial({ color: 0x6d28d9, roughness: 0.45, metalness: 0.06 });
    const base = new THREE.Mesh(new THREE.CylinderGeometry(1.25, 1.45, 0.45, 24), baseMat);
    base.position.y = 0.22;
    base.castShadow = true;
    base.receiveShadow = true;
    this.mesh.add(base);

    const post = new THREE.Mesh(
      new THREE.CylinderGeometry(0.55, 0.66, 2.5, 24),
      new THREE.MeshStandardMaterial({ color: PALETTE.sweeperPost, roughness: 0.42 }),
    );
    post.position.y = 1.35;
    post.castShadow = true;
    this.mesh.add(post);

    const stripeMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.52 });
    for (let y = 0.55; y < 2.45; y += 0.48) {
      const stripe = new THREE.Mesh(new THREE.CylinderGeometry(0.58, 0.68, 0.12, 24), stripeMat);
      stripe.position.y = y;
      this.mesh.add(stripe);
    }

    this.armPivot.position.y = 0.82;
    const arm = new THREE.Mesh(
      new THREE.CapsuleGeometry(0.28, 9.8, 12, 24),
      new THREE.MeshStandardMaterial({ color: PALETTE.sweeperArm, roughness: 0.5 }),
    );
    arm.rotation.z = Math.PI / 2;
    arm.castShadow = true;
    arm.receiveShadow = true;
    this.armPivot.add(arm);

    const tipMat = new THREE.MeshStandardMaterial({ color: 0xb8ff5a, roughness: 0.45 });
    for (const x of [-5.2, 5.2]) {
      const cap = new THREE.Mesh(new THREE.SphereGeometry(0.33, 16, 12), tipMat);
      cap.position.x = x;
      cap.castShadow = true;
      this.armPivot.add(cap);
    }
    this.mesh.add(this.armPivot);
  }

  /** Phase offset so different sweeper instances start at different angles
   *  but each is fully determined by clockMs alone. */
  private spinPhase0 = 0;
  lanes() { return [0, 1, 2, 3, 4]; }
  getZ() { return this.s; }
  getLateralOffset() { return Math.sin(this.spin) * 1.2; }
  update({ clockMs }: ObstacleUpdateArgs) {
    this.spin = this.spinPhase0 + (clockMs / 1000) * this.angularVelocity;
    this.armPivot.rotation.y = this.spin;
  }
  passes({ jumping }: ObstaclePassArgs) {
    return jumping;
  }
  hasForceImpact() {
    return true;
  }
  getContact({
    playerS,
    playerLaneOffset,
    playerY,
    playerRadius,
    playerForwardVelocity,
    playerLateralVelocity,
  }: ObstacleContactArgs): ObstacleContact | null {
    // The green arm is a moving capsule in obstacle-local (lateral, course)
    // space. Contact is checked against the actual bar, not broad lanes.
    if (playerY > 1.25) return null;
    const px = playerLaneOffset;
    const pz = playerS - this.s;
    const ux = Math.cos(this.spin);
    const uz = -Math.sin(this.spin);
    const along = THREE.MathUtils.clamp(px * ux + pz * uz, -this.armHalfLength, this.armHalfLength);
    const closestX = ux * along;
    const closestZ = uz * along;
    const dx = px - closestX;
    const dz = pz - closestZ;
    const radius = this.armRadius + playerRadius;
    const distSq = dx * dx + dz * dz;
    if (distSq > radius * radius) return null;
    const dist = Math.sqrt(Math.max(0.0001, distSq));

    // Tangential velocity of the bar at the contact point: w × r.
    const barVelL = -this.angularVelocity * closestZ;
    const barVelS = this.angularVelocity * closestX;
    const relL = barVelL - playerLateralVelocity;
    const relS = barVelS - playerForwardVelocity;
    let normalL = dx / dist;
    let normalS = dz / dist;

    // If the player is almost exactly centered on the arm, use the direction
    // the bar is sweeping so they are carried by the hit instead of jittering.
    if (dist < 0.08) {
      const speed = Math.hypot(barVelL, barVelS);
      if (speed > 0.001) {
        normalL = barVelL / speed;
        normalS = barVelS / speed;
      }
    }
    const relativeSpeed = Math.max(0, relL * normalL + relS * normalS);
    return {
      normalS,
      normalL,
      penetration: radius - dist,
      relativeSpeed,
      obstacleVelocityS: barVelS,
      obstacleVelocityL: barVelL,
      isForceful: relativeSpeed > 2.0 || Math.hypot(barVelL, barVelS) > 3.4,
    };
  }
}

/* ── BOUNCING BALL ──────────────────────────────────────────────────────────
 * Fall Guys "Hit Parade" style ball: a smooth, candy-striped sphere that
 * spawns at the top of the ramp, rolls and bounces down toward the player,
 * resets when it falls behind. Round and squishy-looking, no cannons.
 */
/** Deterministic per-(ball, wave, channel) pseudo-random in [0..1).
 *  Replaces Math.random for things that need to be consistent across clients
 *  (initial cannon velocities, spawn jitter, etc.). */
function ballRand(id: number, wave: number, channel: number): number {
  let h = id * 73856093 + wave * 19349663 + channel * 83492791;
  h = (h ^ (h >>> 13)) >>> 0;
  h = Math.imul(h, 0x45d9f3b) >>> 0;
  h = (h ^ (h >>> 16)) >>> 0;
  return h / 0x100000000;
}

const BALL_PALETTE: [number, number][] = [
  [0xff5fa7, 0xffffff],
  [0xff8ad8, 0xfff6c2],
  [0x6bd0ff, 0xffffff],
  [0xffd166, 0xff7eb9],
];

function makeBallTexture(c1: number, c2: number): THREE.CanvasTexture {
  const size = 256;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  const hex = (c: number) => "#" + c.toString(16).padStart(6, "0");
  ctx.fillStyle = hex(c1);
  ctx.fillRect(0, 0, size, size);
  ctx.fillStyle = hex(c2);
  const stripeH = size / 6;
  for (let i = 0; i < 6; i += 2) {
    ctx.fillRect(0, i * stripeH, size, stripeH);
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
}

/** Snowball texture — soft white base with random light-blue speckles to read
 *  as packed snow against the dark blue ice course. */
let _snowballTexture: THREE.CanvasTexture | null = null;
function snowballTexture(): THREE.CanvasTexture {
  if (_snowballTexture) return _snowballTexture;
  const size = 256;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "#f6fbff";
  ctx.fillRect(0, 0, size, size);
  ctx.fillStyle = "rgba(149, 196, 232, 0.55)";
  for (let i = 0; i < 90; i++) {
    const x = Math.random() * size;
    const y = Math.random() * size;
    const r = 2 + Math.random() * 4;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
  for (let i = 0; i < 30; i++) {
    const x = Math.random() * size;
    const y = Math.random() * size;
    const r = 1 + Math.random() * 3;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  _snowballTexture = new THREE.CanvasTexture(canvas);
  _snowballTexture.colorSpace = THREE.SRGBColorSpace;
  _snowballTexture.anisotropy = 4;
  return _snowballTexture;
}

class BouncingBall implements ObstacleController {
  kind: ObstacleKind = "bouncing-ball";
  mesh: THREE.Group;
  resolved = false;
  mass = 14;
  private radius = 1.15;
  private body: THREE.Mesh;
  private state: "ready" | "rolling" = "rolling";
  private startS: number;
  private startLane: number;
  private s: number;
  private lateralOffset: number;
  private velocityS = -8; // toward the player (decreasing s)
  private velocityL = 0;
  private airY = 0;
  private airVel = 4.5;
  /** -1 = left cannon, +1 = right cannon. Set deterministically from `id`
   *  so balls always emerge from the same side they spawned from. */
  private cannonSide: 1 | -1 = 1;
  /** Wave number — incremented on each respawn. Used to derive deterministic
   *  per-spawn random values that are still identical across MP clients. */
  private waveCount = 0;
  private spinAxis = new THREE.Vector3(1, 0, 0);
  private path?: Path;
  private respawnAt = 0;
  private playerLowestS = 0;
  private cycleOffset: number;
  constructor(public id: number, z: number, public lane: number, path?: Path, opts?: { snowy?: boolean }) {
    this.mesh = new THREE.Group();
    this.startS = z;
    this.startLane = lane;
    this.s = z;
    this.path = path;
    this.cannonSide = (id % 2 === 0 ? -1 : 1);
    // Initial cannon emergence — far side, high up, arc downward inward.
    this.lateralOffset = this.cannonSide * 8.2;
    this.airY = 5.5;
    this.airVel = 1.4;
    this.velocityS = -9;
    this.velocityL = -this.cannonSide * 4;
    this.cycleOffset = (id * 137) % 2200;
    const material = opts?.snowy
      ? new THREE.MeshStandardMaterial({
          map: snowballTexture(),
          roughness: 0.78,
          metalness: 0.0,
        })
      : (() => {
          const [c1, c2] = BALL_PALETTE[id % BALL_PALETTE.length]!;
          return new THREE.MeshStandardMaterial({
            map: makeBallTexture(c1, c2),
            roughness: 0.4,
            metalness: 0.04,
          });
        })();
    this.body = new THREE.Mesh(
      new THREE.SphereGeometry(this.radius, 32, 24),
      material,
    );
    this.body.castShadow = true;
    this.body.receiveShadow = false;
    this.mesh.add(this.body);
    this.placeBody();
  }
  getLateralOffset() { return this.lateralOffset; }
  lanes() {
    const nearest = nearestLane(this.lateralOffset);
    const lanes = [nearest];
    const center = LANE_POSITIONS[nearest]!;
    if (Math.abs(this.lateralOffset - center) > 0.7) {
      const dir = this.lateralOffset > center ? 1 : -1;
      const next = nearest + dir;
      if (next >= 0 && next < LANE_POSITIONS.length) lanes.push(next);
    }
    return lanes;
  }
  getZ() { return this.s; }
  update(args: ObstacleUpdateArgs) {
    const { dt, now, playerZ } = args;
    this.playerLowestS = playerZ;
    if (this.state === "ready") {
      if (now >= this.respawnAt) {
        // Re-fire from the cannon — same side, high up, inward+downward arc.
        // Spawn parameters are deterministic per (id, waveCount) so every
        // client computes the same initial trajectory and balls land in the
        // same place. (waveCount increments on each respawn so the same ball
        // gets varied trajectories over its lifetime.)
        this.waveCount++;
        const r1 = ballRand(this.id, this.waveCount, 1);
        const r2 = ballRand(this.id, this.waveCount, 2);
        this.state = "rolling";
        this.s = this.startS;
        this.lateralOffset = this.cannonSide * 8.2;
        this.airY = 5.5;
        this.airVel = 1.4;
        // Wider direction spread (~30° fan) so consecutive shots from the
        // same cannon don't follow the same line — matches the visual
        // ±15° barrel sweep.
        this.velocityS = -8 - r1 * 3;
        this.velocityL = -this.cannonSide * (1.5 + r2 * 6.5);
        this.mesh.visible = true;
        this.resolved = false;
      } else {
        this.mesh.visible = false;
        return;
      }
    }
    // Smooth roll along the path. Slope makes the ball roll faster down.
    const slope = this.path?.heightSlopeAt(this.s) ?? 0; // positive = uphill
    // Ball is going down the hill (negative velocityS); negative slope means
    // the next position is lower → accelerate the ball.
    this.velocityS += -slope * 14 * dt;
    // Cap maximum downhill speed (and prevent reversal).
    this.velocityS = Math.max(-22, Math.min(-3, this.velocityS));
    // Mild lateral drift damping; balls wander a bit.
    this.velocityL *= Math.exp(-0.5 * dt);
    if (Math.abs(this.velocityL) < 0.08) this.velocityL = 0;

    this.s += this.velocityS * dt;
    this.lateralOffset += this.velocityL * dt;
    // Bounce off the path edges.
    const half = 5.35;
    if (this.lateralOffset < -half) {
      this.lateralOffset = -half;
      this.velocityL = Math.abs(this.velocityL) * 0.7 + 1.2;
    } else if (this.lateralOffset > half) {
      this.lateralOffset = half;
      this.velocityL = -(Math.abs(this.velocityL) * 0.7 + 1.2);
    }

    // Vertical: gravity always pulls. The ground only catches the ball when
    // it's NOT in the path's gap range — over the gap, the ball just keeps
    // falling like any object in mid-air.
    this.airVel -= 22 * dt;
    this.airY += this.airVel * dt;
    const gap = this.path?.hitParade;
    const overGap = gap != null && this.s >= gap.gapStart && this.s <= gap.gapEnd;
    if (!overGap && this.airY <= 0) {
      this.airY = 0;
      // Re-bounce — ~70% restitution, never below a minimum to keep the show going.
      const restitution = Math.max(3.8, Math.abs(this.airVel) * 0.7);
      // Bounce SFX with distance falloff (15m falloff radius). Skip the very
      // weakest bounces so a ball settling near the player doesn't machinegun
      // the speaker, and skip frames where the player is far away.
      if (restitution > 4.5) {
        const dist = Math.abs(this.s - this.playerLowestS);
        if (dist < 18) {
          const proximity = 1 - dist / 18; // 1 = at player, 0 = at 18m
          const energy = Math.min(1, restitution / 12);
          ballBounceSfx(proximity * energy * 0.85);
        }
      }
      this.airVel = restitution;
      this.spinAxis.set(
        (Math.random() - 0.5) * 0.6,
        (Math.random() - 0.3) * 0.3,
        1,
      ).normalize();
    }
    // Ball fell off the bottom of the world — recycle from the cannon
    // every ~4s, matching the visual cannon firing cadence.
    if (this.airY < -14) {
      this.state = "ready";
      this.mesh.visible = false;
      this.resolved = true;
      this.respawnAt = now + 4000;
      return;
    }

    this.placeBody();
    // Rolling spin: rotate around X proportional to forward motion.
    const rollAngle = (this.velocityS * dt) / this.radius;
    this.body.rotateOnWorldAxis(this.spinAxis, rollAngle);

    // ── Ball ↔ Spinner collisions ──────────────────────────────────────
    // A spinner's arm sits ~0.95m high; the ball center is at radius + airY
    // above the ground. The ball can only hit the arm when its center y is
    // close enough to arm-height (≤ ball radius + arm radius slop). The post
    // is solid all the way up — always collidable.
    if (args.peers) {
      const ballY = this.radius + this.airY;
      for (const peer of args.peers) {
        if (!isSpinner(peer)) continue;
        // Cheap pre-filter — skip spinners far away in arc length.
        if (Math.abs(peer.getZ() - this.s) > 5) continue;
        const armY = 0.95;
        const armSlop = 0.65; // how far the ball can be off arm height and still touch
        const armReachable = Math.abs(ballY - armY) < this.radius + armSlop;
        const contact = peer.ballContact(this.s, this.lateralOffset, this.radius);
        if (!contact) continue;
        // Skip arm-only contact if the ball is too high/low to actually touch it
        // (post-touch always counts).
        const isPostHit = peer.getZ() === peer.getZ() && Math.hypot(this.s - peer.getZ(), this.lateralOffset) < 0.22 + this.radius;
        if (!isPostHit && !armReachable) continue;
        // Resolve penetration: push ball out along the contact normal.
        this.s += contact.normalS * contact.penetration;
        this.lateralOffset += contact.normalL * contact.penetration;
        // Reflect velocity off the normal, with ~70% restitution. Use velocity
        // relative to the arm so the arm imparts its sweep speed onto the ball.
        const relS = this.velocityS - contact.barVelS;
        const relL = this.velocityL - contact.barVelL;
        const dot = relS * contact.normalS + relL * contact.normalL;
        if (dot < 0) {
          const restitution = 0.7;
          const newRelS = relS - (1 + restitution) * dot * contact.normalS;
          const newRelL = relL - (1 + restitution) * dot * contact.normalL;
          this.velocityS = newRelS + contact.barVelS;
          this.velocityL = newRelL + contact.barVelL;
          // Kick the ball slightly upward — collisions make it bounce off rather
          // than just slide along the bar.
          this.airVel = Math.max(this.airVel, 3.2);
          // Bounce SFX on spinner hit, scaled by impact strength.
          const impact = Math.min(1, Math.abs(dot) / 6);
          const dist = Math.abs(this.s - this.playerLowestS);
          if (dist < 22 && impact > 0.15) {
            const prox = 1 - dist / 22;
            ballBounceSfx(prox * impact * 0.9);
          }
        }
        // Clamp to sane range so a chain of contacts can't shoot the ball.
        this.velocityS = THREE.MathUtils.clamp(this.velocityS, -28, 14);
        this.velocityL = THREE.MathUtils.clamp(this.velocityL, -16, 16);
      }
    }

    // ── Ball ↔ Moving platform (sphere-vs-AABB) ────────────────────────
    // The platform is a solid box in (s, y, l) space. Find the closest
    // point on the box to the ball center; if the distance is less than
    // the ball radius, we're penetrating. Push the ball out and reflect
    // its velocity relative to the platform's own velocity.
    if (args.platforms && args.platforms.length > 0) {
      const ballY = this.radius + this.airY; // path-local ball center height
      for (const pf of args.platforms) {
        // Cheap pre-filter — skip far-away platforms.
        if (Math.abs(pf.s - this.s) > pf.halfDepth + this.radius + 0.2) continue;
        if (Math.abs(this.lateralOffset) > pf.halfWidth + this.radius + 0.2) continue;
        // Platform AABB center y = topY - halfHeight (path-local: topY is
        // measured in world y, but in this hit-parade flat section
        // path.heightAt is 0, so topY ≈ halfHeight*2 = thickness. Use
        // halfHeight directly as the center y above the ground.
        const platCenterY = pf.halfHeight; // ground=0 here
        // Closest point on the AABB to the ball center.
        const cS = Math.max(pf.s - pf.halfDepth, Math.min(this.s, pf.s + pf.halfDepth));
        const cL = Math.max(-pf.halfWidth, Math.min(this.lateralOffset, pf.halfWidth));
        const cY = Math.max(platCenterY - pf.halfHeight, Math.min(ballY, platCenterY + pf.halfHeight));
        const dS = this.s - cS;
        const dL = this.lateralOffset - cL;
        const dY = ballY - cY;
        const distSq = dS * dS + dL * dL + dY * dY;
        const r = this.radius;
        if (distSq >= r * r) continue;
        const dist = Math.sqrt(Math.max(0.0001, distSq));
        // Contact normal points from closest-AABB-point to ball center.
        let nS = dS / dist;
        let nL = dL / dist;
        let nY = dY / dist;
        // If the ball is exactly at the surface (dist tiny), pick a sane
        // default normal — pop up out of the top.
        if (dist < 0.001) {
          nS = 0; nL = 0; nY = 1;
        }
        const pen = r - dist;
        // Push ball out of penetration along the normal.
        this.s += nS * pen;
        this.lateralOffset += nL * pen;
        this.airY += nY * pen;
        if (this.airY < 0) this.airY = 0;
        // Reflect ball velocity off the platform's surface. Treat platform
        // as having velocityS only (no vertical/lateral motion currently).
        const relS = this.velocityS - pf.velocityS;
        const relL = this.velocityL;
        const relY = this.airVel; // ball vertical velocity vs platform (0)
        const dot = relS * nS + relL * nL + relY * nY;
        if (dot < 0) {
          const restitution = 0.62;
          const k = (1 + restitution) * dot;
          this.velocityS = relS - k * nS + pf.velocityS;
          this.velocityL = relL - k * nL;
          this.airVel = relY - k * nY;
          // Bounce SFX on platform hit.
          const impact = Math.min(1, Math.abs(dot) / 6);
          const dist = Math.abs(this.s - this.playerLowestS);
          if (dist < 22 && impact > 0.15) {
            const prox = 1 - dist / 22;
            ballBounceSfx(prox * impact * 0.85);
          }
        }
        // Bias upward bounce slightly if we hit the top — makes the show pop.
        if (nY > 0.5 && this.airVel < 2.4) this.airVel = Math.max(this.airVel, 3.4);
        // Clamp to sane range.
        this.velocityS = THREE.MathUtils.clamp(this.velocityS, -28, 14);
        this.velocityL = THREE.MathUtils.clamp(this.velocityL, -16, 16);
        this.airVel = THREE.MathUtils.clamp(this.airVel, -24, 24);
      }
    }

    // Reset if the ball passed the player by 16m (off-screen behind).
    if (this.s < playerZ - 16) {
      this.state = "ready";
      this.mesh.visible = false;
      this.resolved = true;
      // Stagger respawns so balls cascade rather than spawn in a clump.
      this.respawnAt = now + 1400 + (this.cycleOffset % 1100);
    }
  }
  passes() { return false; }
  hasForceImpact() { return true; }
  getContact({
    playerS,
    playerLaneOffset,
    playerY,
    playerRadius,
    playerForwardVelocity,
    playerLateralVelocity,
  }: ObstacleContactArgs): ObstacleContact | null {
    if (this.state !== "rolling") return null;
    // Ball center y is ground + radius + airY.
    const ballY = this.radius + this.airY;
    if (playerY > ballY + this.radius + 0.4) return null;
    const dz = playerS - this.s;
    const dl = playerLaneOffset - this.lateralOffset;
    const radius = this.radius + playerRadius;
    const distSq = dz * dz + dl * dl;
    if (distSq > radius * radius) return null;
    const dist = Math.sqrt(Math.max(0.0001, distSq));
    const normalS = dz / dist;
    const normalL = dl / dist;
    const relS = this.velocityS - playerForwardVelocity;
    const relL = this.velocityL - playerLateralVelocity;
    const relativeSpeed = Math.max(0, relS * normalS + relL * normalL);
    return {
      normalS,
      normalL,
      penetration: radius - dist,
      relativeSpeed,
      obstacleVelocityS: this.velocityS,
      obstacleVelocityL: this.velocityL,
      isForceful: true,
    };
  }
  impact({ playerLaneOffset }: ObstacleImpactArgs) {
    const side = THREE.MathUtils.clamp(this.lateralOffset - playerLaneOffset, -1.2, 1.2);
    this.velocityS = Math.max(-this.velocityS * 0.35, 3);
    this.velocityL += side * 2.4;
    this.airVel = Math.max(this.airVel, 5);
    void this.playerLowestS;
  }
  applyPhysicsImpulse(forward: number, lateral: number) {
    this.velocityS += forward / this.mass;
    this.velocityL += lateral / this.mass;
    this.airVel += Math.max(0, Math.abs(forward) * 0.05);
  }
  getRadius() { return this.radius; }
  getVelocityS() { return this.velocityS; }
  getVelocityL() { return this.velocityL; }
  getVelocityY() { return this.airVel; }
  applyVelocityImpulse(dS: number, dL: number, dY: number) {
    this.velocityS += dS;
    this.velocityL += dL;
    this.airVel += dY;
  }
  private placeBody() {
    placeOnPath(this.mesh, this.path, this.s, this.lateralOffset);
    this.body.position.set(0, this.radius + this.airY, 0);
  }
}

/* ── SMALL SPINNER ──────────────────────────────────────────────────────────
 * A short horizontal bar spinning on a vertical post — covers ~2 lanes, fast
 * enough that you have to time the dodge. Used on the Hit Parade hill.
 */
class Spinner implements ObstacleController {
  kind: ObstacleKind = "spinner";
  mesh = new THREE.Group();
  resolved = false;
  mass = 9;
  private armPivot = new THREE.Group();
  private s: number;
  private spin: number;
  private angularVelocity = 3.4;
  private armHalfLength = 1.9;
  private armRadius = 0.22;
  private baseLateral = 0;
  constructor(public id: number, z: number, _lane: number, path?: Path, opts?: { fat?: boolean; lateral?: number; spinDir?: 1 | -1; long?: boolean }) {
    this.s = z;
    this.baseLateral = opts?.lateral ?? 0;
    placeOnPath(this.mesh, path, z, this.baseLateral);
    this.spinPhase0 = (id * 1.37) % (Math.PI * 2);
    this.spin = this.spinPhase0;
    const dir = opts?.spinDir ?? 1;
    this.angularVelocity = (opts?.fat ? 1.2 : 3.0 + ((id * 0.7) % 1.4)) * dir;
    if (opts?.fat) {
      this.armHalfLength = 5.0;
      this.armRadius = 0.55;
    } else if (opts?.long) {
      // Long thin arm — reaches all the way across the track so the
      // player can't sneak past the rim. Slower angular speed than the
      // default short spinner so the sweep is readable.
      this.armHalfLength = 5.5;
      this.armRadius = 0.28;
      this.angularVelocity = (1.6 + ((id * 0.5) % 0.6)) * dir;
    }

    const baseMat = new THREE.MeshStandardMaterial({ color: 0x7d3df0, roughness: 0.5 });
    const base = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.7, 0.3, 18), baseMat);
    base.position.y = 0.15;
    base.castShadow = true;
    this.mesh.add(base);

    const post = new THREE.Mesh(
      new THREE.CylinderGeometry(0.18, 0.22, 0.9, 16),
      new THREE.MeshStandardMaterial({ color: 0xff5fa7, roughness: 0.4 }),
    );
    // Shortened so the arm sits low enough for a kid's jump to clear it.
    post.position.y = 0.55;
    post.castShadow = true;
    this.mesh.add(post);

    this.armPivot.position.y = 0.55;
    const arm = new THREE.Mesh(
      new THREE.CapsuleGeometry(this.armRadius, this.armHalfLength * 2, 10, 18),
      new THREE.MeshStandardMaterial({ color: 0x6bd0ff, roughness: 0.45 }),
    );
    arm.rotation.z = Math.PI / 2;
    arm.castShadow = true;
    this.armPivot.add(arm);

    const tipMat = new THREE.MeshStandardMaterial({ color: 0xffd166, roughness: 0.45 });
    for (const x of [-this.armHalfLength, this.armHalfLength]) {
      const tip = new THREE.Mesh(new THREE.SphereGeometry(0.26, 14, 10), tipMat);
      tip.position.x = x;
      tip.castShadow = true;
      this.armPivot.add(tip);
    }
    this.mesh.add(this.armPivot);
  }
  lanes() { return [1, 2, 3]; }
  /** Spin offset captured at construction so the rotation phase is
   *  deterministic from clockMs alone, identical on every client. */
  private spinPhase0 = 0;
  getZ() { return this.s; }
  getLateralOffset() { return this.baseLateral + Math.sin(this.spin) * 0.6; }
  update({ clockMs }: ObstacleUpdateArgs) {
    // Deterministic absolute angle = (clockMs/1000) * angularVelocity + phase0.
    // Avoids drift accumulating from dt jitter across clients.
    this.spin = this.spinPhase0 + (clockMs / 1000) * this.angularVelocity;
    this.armPivot.rotation.y = this.spin;
  }
  passes({ jumping }: ObstaclePassArgs) {
    // Jumping over the spinning bar clears it (bar now sits at y ≈ 0.55).
    return jumping;
  }
  /** For ball↔spinner: return contact info if a circle at (s, l) intersects
   *  the post (at lateral 0) or the spinning arm. Returns null if no overlap. */
  ballContact(s: number, l: number, ballRadius: number): {
    normalS: number;
    normalL: number;
    penetration: number;
    barVelS: number;
    barVelL: number;
  } | null {
    // Post (vertical pole at the spinner center) — small circle, always solid.
    const postRadius = 0.22;
    const dzPost = s - this.s;
    const dlPost = l - 0;
    const postR = postRadius + ballRadius;
    const postDistSq = dzPost * dzPost + dlPost * dlPost;
    if (postDistSq < postR * postR) {
      const dist = Math.sqrt(Math.max(0.0001, postDistSq));
      return {
        normalS: dzPost / dist,
        normalL: dlPost / dist,
        penetration: postR - dist,
        barVelS: 0,
        barVelL: 0,
      };
    }
    // Spinning arm (capsule rotating around the post).
    const dz = s - this.s;
    const dl = l;
    const ux = Math.cos(this.spin); // along the arm (lateral component)
    const uz = -Math.sin(this.spin); // along the arm (s component)
    const along = THREE.MathUtils.clamp(dl * ux + dz * uz, -this.armHalfLength, this.armHalfLength);
    const closestL = ux * along;
    const closestS = uz * along;
    const dxL = dl - closestL;
    const dxS = dz - closestS;
    const armR = this.armRadius + ballRadius;
    const distSq = dxS * dxS + dxL * dxL;
    if (distSq > armR * armR) return null;
    const dist = Math.sqrt(Math.max(0.0001, distSq));
    // Tangential velocity of the arm at the contact point: ω × r.
    const barVelL = -this.angularVelocity * closestS;
    const barVelS = this.angularVelocity * closestL;
    return {
      normalS: dxS / dist,
      normalL: dxL / dist,
      penetration: armR - dist,
      barVelS,
      barVelL,
    };
  }
  hasForceImpact() { return true; }
  getContact({
    playerS,
    playerLaneOffset,
    playerY,
    playerRadius,
    playerForwardVelocity,
    playerLateralVelocity,
  }: ObstacleContactArgs): ObstacleContact | null {
    if (playerY > 1.4) return null;
    // Spinner post can be offset laterally (two-spinner side-by-side
    // placement on Level 1). Subtract that offset so the arm's pivot is
    // at local origin for the contact maths below.
    const px = playerLaneOffset - this.baseLateral;
    const pz = playerS - this.s;
    const ux = Math.cos(this.spin);
    const uz = -Math.sin(this.spin);
    const along = THREE.MathUtils.clamp(px * ux + pz * uz, -this.armHalfLength, this.armHalfLength);
    const closestX = ux * along;
    const closestZ = uz * along;
    const dx = px - closestX;
    const dz = pz - closestZ;
    const radius = this.armRadius + playerRadius;
    const distSq = dx * dx + dz * dz;
    if (distSq > radius * radius) return null;
    const dist = Math.sqrt(Math.max(0.0001, distSq));
    const barVelL = -this.angularVelocity * closestZ;
    const barVelS = this.angularVelocity * closestX;
    const relL = barVelL - playerLateralVelocity;
    const relS = barVelS - playerForwardVelocity;
    let normalL = dx / dist;
    let normalS = dz / dist;
    if (dist < 0.08) {
      const speed = Math.hypot(barVelL, barVelS);
      if (speed > 0.001) {
        normalL = barVelL / speed;
        normalS = barVelS / speed;
      }
    }
    const relativeSpeed = Math.max(0, relL * normalL + relS * normalS);
    return {
      normalS,
      normalL,
      penetration: radius - dist,
      relativeSpeed,
      obstacleVelocityS: barVelS,
      obstacleVelocityL: barVelL,
      isForceful: true,
    };
  }
}

/* ── factory ─────────────────────────────────────────────────────────────── */
export function createObstacle(kind: ObstacleKind, opts: BaseOpts): ObstacleController {
  switch (kind) {
    case "barrier": return new Barrier(opts.id, opts.z, opts.lane, opts.path);
    case "crate": return new Crate(opts.id, opts.z, opts.lane, opts.path);
    case "lava": return new Lava(opts.id, opts.z, opts.lane, opts.path);
    case "log": return new FallingLog(opts.id, opts.z, opts.lane, opts.path);
    case "swing-log": return new SwingingLog(opts.id, opts.z, opts.lane, opts.path);
    case "boulder": return new RollingBoulder(opts.id, opts.z, opts.lane, opts.path);
    case "spike-roller": return new SpikeRoller(opts.id, opts.z, opts.lane, opts.path);
    case "sweeper": return new RotatingSweeper(opts.id, opts.z, opts.lane, opts.path);
    case "bouncing-ball": return new BouncingBall(opts.id, opts.z, opts.lane, opts.path);
    case "snowball": return new BouncingBall(opts.id, opts.z, opts.lane, opts.path, { snowy: true });
    case "spinner": return new Spinner(opts.id, opts.z, opts.lane, opts.path, { lateral: opts.lateral, spinDir: opts.spinDir, long: opts.long });
    case "ice-spinner": return new Spinner(opts.id, opts.z, opts.lane, opts.path, { fat: true });
  }
}

export function isFallingLog(o: ObstacleController): o is FallingLog {
  return o.kind === "log";
}

export function isSpinner(o: ObstacleController): o is Spinner {
  return o.kind === "spinner";
}

function nearestLane(offset: number): number {
  let bestLane = 0;
  let bestDist = Infinity;
  for (let i = 0; i < LANE_POSITIONS.length; i++) {
    const d = Math.abs(offset - LANE_POSITIONS[i]!);
    if (d < bestDist) {
      bestDist = d;
      bestLane = i;
    }
  }
  return bestLane;
}
